import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import * as alignmentviewutil from 'analyses/alignment/AlignmentViewUtil';
import notifications from 'notifications';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import BaseEditableField from 'ui-library/BaseEditableField';
import useDraftState from 'ui-library/hooks/useDraftState';
import TextInput from 'ui-library/inputs/TextInput';
import {isEnterKey, isEscapeKey} from 'util/Keyboard';
import {isServerRequestError} from 'util/Net';
import {InputChangeEvent} from 'util/Types';

export interface NameInputProps {
  allowFeatureEdits: boolean;
  analysisName: string | undefined;
  loaded: boolean;
  updateName: (name: string) => Promise<void>;
}

export default function NameInput({
  allowFeatureEdits,
  analysisName,
  loaded,
  updateName,
}: NameInputProps): JSX.Element {
  const [saving, setSaving] = useState(false);
  const {data, isEditing, startEditing, stopEditing, updateDraftData} = useDraftState({name: analysisName});

  async function saveValue(): Promise<void> {
    if (data.name != null && data.name !== analysisName) {
      if (data.name.length === 0) {
        notifications.error('Please specify a name for your DNA / RNA alignment.');
        return;
      } else if (data.name.length > 255) {
        notifications.error('Please specify a shorter name for your DNA / RNA alignment.');
        return;
      }
      setSaving(true);
      stopEditing();
      try {
        await updateName(data.name);
      } catch (err) {
        if (isServerRequestError(err)) {
          alignmentviewutil.notifyError(err);
          return;
        }
        throw err;
      } finally {
        setSaving(false);
      }
    } else {
      stopEditing();
    }
  }

  return (
    <div className={css(styles.nameInput)}>
      <BaseEditableField
        editor={
          <TextInput
            className={css(styles.nameFont)}
            value={data.name}
            onChange={(e: InputChangeEvent) => {
              updateDraftData({name: e.target.value});
            }}
            onKeyUp={(e) => {
              if (isEnterKey(e)) {
                saveValue();
              } else if (isEscapeKey(e)) {
                stopEditing();
              }
            }}
            autoFocus={true}
          />
        }
        viewer={<span className={`u-ellipsis-text ${css(styles.nameFont)}`}>{data.name}</span>}
        isBeingEdited={isEditing}
        isCompleteEnabled={true}
        isWritable={loaded && allowFeatureEdits && !saving}
        onCancel={stopEditing}
        onComplete={saveValue}
        onEdit={startEditing}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  nameInput: {
    width: '200px',
  },
  nameFont: {
    fontSize: '18px',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
});
